import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useDealerLog, LOG_ACTIONS } from "@/hooks/useDealerLog";

interface Category {
  id: string;
  name: string;
}

export default function CategoriesPage() {
  const { user } = useAuth();
  const dealerId = user?.dealerId || user?.id || "";
  const { logAction } = useDealerLog();

  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: fetchErr } = await supabase
        .from("categories")
        .select("id, name")
        .eq("dealer_id", dealerId)
        .order("name");
      if (fetchErr) throw new Error(fetchErr.message);
      setCategories(data || []);
    } catch (err: any) {
      setError(err.message || "Kategoriler yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    if (dealerId) fetchCategories();
  }, [dealerId, fetchCategories]);

  const openAdd = () => { setEditing(null); setName(""); setShowModal(true); };
  const openEdit = (c: Category) => { setEditing(c); setName(c.name); setShowModal(true); };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        const { error: updateErr } = await supabase
          .from("categories")
          .update({ name: name.trim() })
          .eq("id", editing.id);
        if (updateErr) throw new Error(updateErr.message);
        logAction(dealerId, user?.id || null, LOG_ACTIONS.CATEGORY_UPDATE, `"${editing.name}" → "${name.trim()}" kategorisi güncellendi`);
      } else {
        const { error: insertErr } = await supabase
          .from("categories")
          .insert({ dealer_id: dealerId, name: name.trim() });
        if (insertErr) throw new Error(insertErr.message);
        logAction(dealerId, user?.id || null, LOG_ACTIONS.CATEGORY_ADD, `"${name.trim()}" kategorisi eklendi`);
      }
      setShowModal(false);
      fetchCategories();
    } catch (err: any) {
      setError(err.message || "Kayıt sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async (id: string, name: string) => {
    if (!confirm("Bu kategoriyi silmek istediğinize emin misiniz?")) return;
    try {
      const { error: delErr } = await supabase.from("categories").delete().eq("id", id);
      if (delErr) throw new Error(delErr.message);
      logAction(dealerId, user?.id || null, LOG_ACTIONS.CATEGORY_DELETE, `"${name}" kategorisi silindi`);
      fetchCategories();
    } catch (err: any) {
      setError(err.message || "Silme sırasında hata oluştu.");
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {error && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={() => setError("")} className="ml-auto text-accent-700 font-medium cursor-pointer">Kapat</button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="categories-title">Kategoriler</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">Ürün kategorilerini yönetin</p>
        </div>
        <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i> Kategori Ekle
        </button>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-foreground-400">
            <i className="ri-loader-4-line animate-spin text-3xl block mb-2"></i>
            <p className="text-sm">Yükleniyor...</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto hidden md:block">
              <table className="w-full text-sm min-w-[400px]">
                <thead>
                  <tr className="bg-background-100 text-left">
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Kategori Adı</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-4 py-12 text-center text-foreground-400">
                        <i className="ri-folder-line text-3xl block mb-2"></i>
                        <p className="text-sm">Henüz kategori eklenmemiş</p>
                      </td>
                    </tr>
                  ) : (
                    categories.map((c) => (
                      <tr key={c.id} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                        <td className="px-4 py-3 font-medium text-foreground-900">{c.name}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => openEdit(c)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                            <button onClick={() => deleteCategory(c.id, c.name)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="md:hidden divide-y divide-background-200/70">
              {categories.length === 0 ? (
                <div className="px-4 py-12 text-center text-foreground-400">
                  <i className="ri-folder-line text-3xl block mb-2"></i>
                  <p className="text-sm">Henüz kategori eklenmemiş</p>
                </div>
              ) : (
                categories.map((c) => (
                  <div key={c.id} className="px-4 py-3 flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-foreground-900">{c.name}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(c)} className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer"><i className="ri-edit-line"></i></button>
                      <button onClick={() => deleteCategory(c.id, c.name)} className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer"><i className="ri-delete-bin-line"></i></button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950"><a id="category-form">{editing ? "Kategori Düzenle" : "Yeni Kategori"}</a></h4>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-foreground-500 mb-1">Kategori Adı</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" placeholder="Kategori adı girin" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={save} disabled={saving} className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap">
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}